// Line glyphs for the 12 sounds (glass skin: a blue line icon in a soft tile, prototype /proto/ › Sounds),
// and the library's categories. The felt skin keeps the felt pictures from catalog.ts.
import type { SoundId } from '@/features/sound/catalog';

const PATHS: Record<SoundId, React.ReactNode> = {
  shush: <path d="M3 12c2-4 4-4 6 0s4 4 6 0 4-4 6 0" />,
  white: <>{[[6, 8], [12, 6], [18, 9], [8, 14], [15, 13], [11, 19], [18, 17], [5, 19]].map(([cx, cy]) => <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="1" />)}</>,
  womb: <><circle cx="12" cy="12" r="9" /><path d="M12 16s-4-2.4-4-5a2 2 0 0 1 4-1 2 2 0 0 1 4 1c0 2.6-4 5-4 5Z" /></>,
  heart: <path d="M3 12h4l2-5 3 10 2-5h7" />,
  hair: <><path d="M4 7h10a4 4 0 0 1 0 8H4z" /><path d="M9 15l-1 6M18 9h3M18 13h3" /></>,
  fan: <><circle cx="12" cy="12" r="2" /><path d="M12 10c0-5 4-7 5-4s-3 4-5 4M14 12c5 0 7 4 4 5s-4-3-4-5M12 14c0 5-4 7-5 4s3-4 5-4M10 12c-5 0-7-4-4-5s4 3 4 5" /></>,
  vacuum: <><path d="M6 20h9a3 3 0 0 0 0-6H9l-3-10" /><circle cx="7" cy="20" r="1" /></>,
  washer: <><rect x="4" y="3" width="16" height="18" rx="3" /><circle cx="12" cy="13" r="4.5" /><path d="M7 6.5h.01M10 6.5h.01" /></>,
  car: <><path d="M4 16v-3l2-5h12l2 5v3z" /><circle cx="8" cy="16.5" r="1.8" /><circle cx="16" cy="16.5" r="1.8" /></>,
  rain: <><path d="M7 14a4 4 0 0 1 .5-8 5 5 0 0 1 9.5 1.5A3.3 3.3 0 0 1 17 14z" /><path d="M8 18l-1 2M12 18l-1 2M16 18l-1 2" /></>,
  ocean: <path d="M2 9c2.5-2 4.5-2 7 0s4.5 2 7 0 4.5-2 6 0M2 15c2.5-2 4.5-2 7 0s4.5 2 7 0 4.5-2 6 0" />,
  music: <><path d="M9 18V5l11-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="17" cy="16" r="3" /></>,
};

/** The sound's line glyph in a soft tile (`className` sizes it). */
export function SoundGlyph({ id, className = 'snd-glyph' }: { id: SoundId; className?: string }) {
  return (
    <span className={className} aria-hidden="true">
      <svg viewBox="0 0 24 24">{PATHS[id]}</svg>
    </span>
  );
}

export type SoundCat = 'all' | 'womb' | 'noise' | 'nature' | 'lullaby';
export const CATS: SoundCat[] = ['all', 'womb', 'noise', 'nature', 'lullaby'];
export const SOUND_CAT: Record<SoundId, Exclude<SoundCat, 'all'>> = {
  shush: 'womb', womb: 'womb', heart: 'womb',
  white: 'noise', hair: 'noise', fan: 'noise', vacuum: 'noise', washer: 'noise', car: 'noise',
  rain: 'nature', ocean: 'nature',
  music: 'lullaby',
};

/** "29:41" until the timer ends. */
export function mmss(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export function PlayIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5.6v12.8c0 1 1.1 1.6 2 1.1l10-6.4c.8-.5.8-1.7 0-2.2L10 4.5c-.9-.6-2 .1-2 1.1Z" /></svg>;
}
export function PauseIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="6" y="4.5" width="4.2" height="15" rx="2.1" /><rect x="13.8" y="4.5" width="4.2" height="15" rx="2.1" /></svg>;
}
